import { EmissionBudgetItem, EmissionBudgetResponse, getEmissionBudget } from "@/api/services/BuildingProject";
import * as Sentry from "@sentry/vue";
import { ref } from "vue";

export type EmissionBudgetState = {
  data?: EmissionBudgetItem;
  error: boolean;
  loading: boolean;
};

export const useEmissionBudgets = () => {
  const emissionBudgetsByProjectId = ref<Record<string, EmissionBudgetState>>({});

  const clearEmissionBudgets = () => {
    emissionBudgetsByProjectId.value = {};
  };

  const clearEmissionBudgetsByProjectIds = (projectIds: string[]) => {
    projectIds.forEach((id) => {
      delete emissionBudgetsByProjectId.value[id];
    });
    emissionBudgetsByProjectId.value = { ...emissionBudgetsByProjectId.value };
  };

  const fetchEmissionBudgets = async (projectIds: string[], forceFetch?: boolean) => {
    const idsToFetch = projectIds.filter((id) => {
      const existing = emissionBudgetsByProjectId.value[id];

      if (forceFetch) return true;

      // Never refetch if it is already loading, has errored, or has data
      if (!existing) return true;
      if (existing.loading) return false;
      if (existing.error) return false;
      if (existing.data) return false;

      return true;
    });

    if (idsToFetch.length === 0) return;

    // Mark as loading immediately to avoid duplicate fetches
    idsToFetch.forEach((id) => {
      const existing = emissionBudgetsByProjectId.value[id];
      emissionBudgetsByProjectId.value[id] = {
        loading: true,
        error: false,
        data: existing?.data,
      };
    });

    // Force a top-level change so watchers on the ref trigger
    emissionBudgetsByProjectId.value = { ...emissionBudgetsByProjectId.value };

    try {
      const res: EmissionBudgetResponse | undefined = await getEmissionBudget(idsToFetch);

      if (!res) {
        // Treat missing response as an error for these ids
        idsToFetch.forEach((id) => {
          const existing = emissionBudgetsByProjectId.value[id];
          emissionBudgetsByProjectId.value[id] = {
            loading: false,
            error: true,
            data: existing?.data,
          };
        });

        // Force a top-level change so watchers on the ref trigger
        emissionBudgetsByProjectId.value = {
          ...emissionBudgetsByProjectId.value,
        };
        return;
      }

      idsToFetch.forEach((id) => {
        const item = res[id];
        emissionBudgetsByProjectId.value[id] = {
          loading: false,
          error: !item,
          data: item ?? emissionBudgetsByProjectId.value[id]?.data,
        };
      });

      // Force a top-level change so watchers on the ref trigger
      emissionBudgetsByProjectId.value = {
        ...emissionBudgetsByProjectId.value,
      };
    } catch (error) {
      idsToFetch.forEach((id) => {
        const existing = emissionBudgetsByProjectId.value[id];
        emissionBudgetsByProjectId.value[id] = {
          loading: false,
          error: true,
          data: existing?.data,
        };
      });

      // Force a top-level change so watchers on the ref trigger
      emissionBudgetsByProjectId.value = {
        ...emissionBudgetsByProjectId.value,
      };

      Sentry.captureException(error);
    }
  };

  return {
    clearEmissionBudgetsByProjectIds,
    clearEmissionBudgets,
    emissionBudgetsByProjectId,
    fetchEmissionBudgets,
  };
};
